import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { apiClient, errorDetail } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EditWithHistory } from '@/components/catalog/EditWithHistory';

interface BulkUploadResult {
  created: number;
  updated: number;
  errors: string[];
}

interface ProviderCost {
  provider: string;
  costSetup: number;
  costMrr: number;
  notes?: string | null;
}

type UnitLabel = 'voice_minute' | 'conversation' | 'report' | 'contact' | 'none';

interface RequiredResource {
  resourceId: string;
  name: string;
  type: string;
  mandatory: boolean;
  setupUrl?: string | null;
  notes?: string | null;
}

interface AIAgent {
  id: string;
  agentId: string;
  agentName: string;
  industries: string[];
  countries: string[];
  tags: string[];
  priceSetupUsd: number;
  priceMrrUsd: number;
  costsSetupUsd: number;
  costsMrrUsd: number;
  unitLabel: UnitLabel;
  includedPerMrr: number;
  tokensPerUse: number;
  overagePriceUsd: number;
  overageCostUsd: number;
  providers: ProviderCost[];
  requiredResources: RequiredResource[];
  notes: string | null;
  status: 'ACTIVE' | 'DRAFT' | 'ARCHIVED';
  createdAt: string;
  updatedAt: string;
}

interface UpsertPayload {
  agentId: string;
  agentName: string;
  industries: string[];
  countries: string[];
  tags: string[];
  priceSetupUsd: number;
  priceMrrUsd: number;
  costsSetupUsd: number;
  costsMrrUsd: number;
  unitLabel: UnitLabel;
  includedPerMrr: number;
  tokensPerUse: number;
  overagePriceUsd: number;
  overageCostUsd: number;
  providers: ProviderCost[];
  requiredResources: RequiredResource[];
  notes: string | null;
  status: 'ACTIVE' | 'DRAFT' | 'ARCHIVED';
}

interface PagedResult<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

interface Facets {
  countries: string[];
  industries: string[];
  tags: string[];
  statuses: string[];
}

const EMPTY: UpsertPayload = {
  agentId: '',
  agentName: '',
  industries: [],
  countries: [],
  tags: [],
  priceSetupUsd: 0,
  priceMrrUsd: 0,
  costsSetupUsd: 0,
  costsMrrUsd: 0,
  unitLabel: 'conversation',
  includedPerMrr: 0,
  tokensPerUse: 0,
  overagePriceUsd: 0,
  overageCostUsd: 0,
  providers: [],
  requiredResources: [],
  notes: null,
  status: 'ACTIVE',
};

const PAGE_SIZE = 25;

function usd(n: number): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);
}

function marginPct(price: number, cost: number): string {
  if (!price) return '—';
  const m = ((price - cost) / price) * 100;
  return `${m.toFixed(0)}%`;
}

export function AIAgentsForIndustriesPage() {
  const [items, setItems] = useState<AIAgent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AIAgent | null>(null);
  const [creating, setCreating] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState<string>('');
  const [industry, setIndustry] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('ACTIVE');
  const [tag, setTag] = useState<string>('');
  const [page, setPage] = useState(0);

  // Facet values for filter dropdowns
  const [facets, setFacets] = useState<Facets>({
    countries: [], industries: [], tags: [], statuses: ['ACTIVE', 'DRAFT', 'ARCHIVED'],
  });

  // Load facets once (rarely change)
  useEffect(() => {
    apiClient
      .get<Facets>('/admin/ai-agents-for-industries/facets')
      .then(({ data }) => setFacets((prev) => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  // Debounce search by 250ms so we don't hit the API on every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (country) params.set('country', country);
    if (industry) params.set('industry', industry);
    if (statusFilter) params.set('status', statusFilter);
    if (tag) params.set('tag', tag);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(page * PAGE_SIZE));

    apiClient
      .get<PagedResult<AIAgent>>(`/admin/ai-agents-for-industries/search?${params}`)
      .then(({ data }) => {
        setItems(data.items);
        setTotal(data.total);
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [debouncedSearch, country, industry, statusFilter, tag, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset to page 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, country, industry, statusFilter, tag]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const clearFilters = () => {
    setSearch('');
    setCountry('');
    setIndustry('');
    setStatusFilter('ACTIVE');
    setTag('');
  };
  const hasActiveFilters = Boolean(search || country || industry || tag || statusFilter !== 'ACTIVE');

  const handleArchive = (id: string) => {
    if (!confirm('¿Archivar este agente?')) return;
    apiClient.delete(`/admin/ai-agents-for-industries/${id}`).then(() => load()).catch(() => {});
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await apiClient.post<BulkUploadResult>(
        '/admin/ai-agents-for-industries/bulk-upload',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setUploadResult(data);
      load();
    } catch (err) {
      setUploadResult({
        created: 0,
        updated: 0,
        errors: [errorDetail(err)],
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">AI Agents for Industries</h1>
          <p className="text-sm text-muted-foreground">
            Catálogo maestro Lopettia · precios sugeridos al order form · USD
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleUpload}
            className="hidden"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'subiendo…' : '↑ Subir CSV'}
          </Button>
          <Button size="sm" onClick={() => setCreating(true)}>
            + Agente
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-3">
        <Input
          placeholder="🔎 buscar por nombre, id, notas…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64"
        />
        <select
          className="rounded border bg-background px-2 py-1.5 text-sm"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
        >
          <option value="">país (todos)</option>
          {facets.countries.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          className="rounded border bg-background px-2 py-1.5 text-sm"
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
        >
          <option value="">industria (todas)</option>
          {facets.industries.map((i) => (
            <option key={i} value={i}>{i}</option>
          ))}
        </select>
        <select
          className="rounded border bg-background px-2 py-1.5 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">status (todos)</option>
          {facets.statuses.map((s) => (
            <option key={s} value={s}>{s.toLowerCase()}</option>
          ))}
        </select>
        {facets.tags.length > 0 && (
          <select
            className="rounded border bg-background px-2 py-1.5 text-sm"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
          >
            <option value="">tag (todos)</option>
            {facets.tags.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        )}
        {hasActiveFilters && (
          <Button size="sm" variant="ghost" onClick={clearFilters}>
            limpiar
          </Button>
        )}
        <div className="ml-auto text-xs text-muted-foreground">
          {total} {total === 1 ? 'resultado' : 'resultados'}
          {totalPages > 1 && ` · página ${page + 1} de ${totalPages}`}
        </div>
      </div>

      {uploadResult && (
        <div
          className={`rounded border px-3 py-2 text-sm ${
            uploadResult.errors.length > 0
              ? 'border-orange-300 bg-orange-50 text-orange-900'
              : 'border-green-300 bg-green-50 text-green-900'
          }`}
        >
          <strong>Bulk upload:</strong> {uploadResult.created} creados ·{' '}
          {uploadResult.updated} actualizados
          {uploadResult.errors.length > 0 && (
            <>
              {' · '}
              {uploadResult.errors.length} errores
              <ul className="mt-1 list-disc pl-5 text-xs">
                {uploadResult.errors.slice(0, 10).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
                {uploadResult.errors.length > 10 && (
                  <li>(+{uploadResult.errors.length - 10} más)</li>
                )}
              </ul>
            </>
          )}
          <button
            onClick={() => setUploadResult(null)}
            className="mt-1 text-xs underline opacity-70"
          >
            descartar
          </button>
        </div>
      )}

      {creating && (
        <AgentForm
          initial={EMPTY}
          onCancel={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load();
          }}
        />
      )}

      {loading && <p className="text-sm text-muted-foreground">cargando…</p>}

      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">agent</th>
              <th className="px-4 py-2 text-left">industrias</th>
              <th className="px-4 py-2 text-left">países</th>
              <th className="px-4 py-2 text-right">setup</th>
              <th className="px-4 py-2 text-right">MRR</th>
              <th className="px-4 py-2 text-left">incluye</th>
              <th className="px-4 py-2 text-right">overage</th>
              <th className="px-4 py-2 text-right">margen</th>
              <th className="px-4 py-2 text-left">recursos</th>
              <th className="px-4 py-2 text-left">status</th>
              <th className="px-4 py-2 text-right">acciones</th>
            </tr>
            <tr className="text-xs">
              <td colSpan={11}></td>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.map((a) => (
              <>
                <tr key={a.id} className={a.status === 'ARCHIVED' ? 'opacity-50' : ''}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{a.agentName}</div>
                    <div className="font-mono text-xs text-muted-foreground">{a.agentId}</div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {a.industries.length === 0 ? (
                        <span className="text-xs text-muted-foreground">cross-industry</span>
                      ) : (
                        a.industries.map((i) => (
                          <span key={i} className="rounded bg-muted px-2 py-0.5 text-xs">
                            {i}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {a.countries.length === 0 ? (
                        <span className="text-xs text-muted-foreground">global</span>
                      ) : (
                        a.countries.map((c) => (
                          <span key={c} className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-900 dark:bg-blue-900/30 dark:text-blue-200">
                            {c}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{usd(a.priceSetupUsd)}</td>
                  <td className="px-4 py-2 text-right font-mono">{usd(a.priceMrrUsd)}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {a.unitLabel === 'none' || a.includedPerMrr === 0 ? (
                      <span>—</span>
                    ) : (
                      <span>
                        {a.includedPerMrr.toLocaleString()} {a.unitLabel.replace('_', ' ')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {a.overagePriceUsd > 0 ? `${usd(a.overagePriceUsd)}/u` : '—'}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {marginPct(a.priceMrrUsd, a.costsMrrUsd)}
                  </td>
                  <td className="px-4 py-2">
                    {a.requiredResources.length === 0 ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <span
                        className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"
                        title={a.requiredResources.map((r) => `${r.name} (${r.type})${r.mandatory ? ' *' : ''}`).join('\n')}
                      >
                        {a.requiredResources.length} {a.requiredResources.length === 1 ? 'recurso' : 'recursos'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={
                        a.status === 'ACTIVE'
                          ? 'text-xs text-green-600'
                          : a.status === 'DRAFT'
                          ? 'text-xs text-yellow-600'
                          : 'text-xs text-muted-foreground'
                      }
                    >
                      {a.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => setEditing(a)}
                      className="text-xs text-primary hover:underline"
                    >
                      editar
                    </button>
                    {a.status === 'ACTIVE' && (
                      <button
                        onClick={() => handleArchive(a.id)}
                        className="ml-3 text-xs text-muted-foreground hover:underline"
                      >
                        archivar
                      </button>
                    )}
                  </td>
                </tr>
                {editing?.id === a.id && (
                  <tr>
                    <td colSpan={11} className="bg-muted/20 px-4 py-4">
                      <EditWithHistory entityType="ai_agent" entityId={a.id} editForm={<AgentForm initial={toPayload(a)} editingId={a.id} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />} />
                    </td>
                  </tr>
                )}
              </>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={11} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {hasActiveFilters
                    ? 'No hay agentes que coincidan con esos filtros · prueba a limpiarlos'
                    : 'No hay agentes en el catálogo · click "+ Agente" para crear el primero'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between rounded-lg border bg-card px-4 py-2 text-sm">
          <div className="text-xs text-muted-foreground">
            mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ← anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              siguiente →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function toPayload(a: AIAgent): UpsertPayload {
  return {
    agentId: a.agentId,
    agentName: a.agentName,
    industries: a.industries,
    countries: a.countries,
    tags: a.tags,
    priceSetupUsd: a.priceSetupUsd,
    priceMrrUsd: a.priceMrrUsd,
    costsSetupUsd: a.costsSetupUsd,
    costsMrrUsd: a.costsMrrUsd,
    unitLabel: a.unitLabel,
    includedPerMrr: a.includedPerMrr,
    tokensPerUse: a.tokensPerUse,
    overagePriceUsd: a.overagePriceUsd,
    overageCostUsd: a.overageCostUsd,
    providers: a.providers,
    requiredResources: a.requiredResources,
    notes: a.notes,
    status: a.status,
  };
}

function AgentForm({
  initial,
  editingId,
  onCancel,
  onSaved,
}: {
  initial: UpsertPayload;
  editingId?: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<UpsertPayload>(initial);
  const [industriesText, setIndustriesText] = useState(initial.industries.join(', '));
  const [countriesText, setCountriesText] = useState(initial.countries.join(', '));
  const [tagsText, setTagsText] = useState(initial.tags.join(', '));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalCostsSetup = form.providers.reduce((s, p) => s + (p.costSetup || 0), 0);
  const totalCostsMrr = form.providers.reduce((s, p) => s + (p.costMrr || 0), 0);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body: UpsertPayload = {
        ...form,
        industries: industriesText
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        countries: countriesText
          .split(',')
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean),
        tags: tagsText
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        // Aggregate totals reflect provider breakdown if user filled providers,
        // otherwise keep the manually-entered totals.
        costsSetupUsd: form.providers.length > 0 ? totalCostsSetup : form.costsSetupUsd,
        costsMrrUsd: form.providers.length > 0 ? totalCostsMrr : form.costsMrrUsd,
      };
      if (editingId) {
        await apiClient.put(`/admin/ai-agents-for-industries/${editingId}`, body);
      } else {
        await apiClient.post('/admin/ai-agents-for-industries', body);
      }
      onSaved();
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  const updateProvider = (idx: number, patch: Partial<ProviderCost>) => {
    setForm({
      ...form,
      providers: form.providers.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    });
  };
  const addProvider = () =>
    setForm({
      ...form,
      providers: [...form.providers, { provider: '', costSetup: 0, costMrr: 0 }],
    });
  const removeProvider = (idx: number) =>
    setForm({ ...form, providers: form.providers.filter((_, i) => i !== idx) });

  return (
    <form onSubmit={submit} className="space-y-4">
      {error && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">agent_id</span>
          <Input
            value={form.agentId}
            onChange={(e) => setForm({ ...form, agentId: e.target.value })}
            placeholder="reception"
            required
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">agent name</span>
          <Input
            value={form.agentName}
            onChange={(e) => setForm({ ...form, agentName: e.target.value })}
            placeholder="Recepción"
            required
          />
        </label>
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-medium uppercase text-muted-foreground">
          países · ISO codes separados por coma · vacío = global (aplica donde sea)
        </span>
        <Input
          value={countriesText}
          onChange={(e) => setCountriesText(e.target.value)}
          placeholder="PR, CO, US"
        />
      </label>

      <label className="block space-y-1">
        <span className="text-xs font-medium uppercase text-muted-foreground">
          tags · separadas por coma · clasificación libre (SMB, pharma, mvp…)
        </span>
        <Input
          value={tagsText}
          onChange={(e) => setTagsText(e.target.value)}
          placeholder="SMB, voice-first, act60"
        />
      </label>

      <label className="block space-y-1">
        <span className="text-xs font-medium uppercase text-muted-foreground">
          industrias · separadas por coma · vacío = cross-industry
        </span>
        <Input
          value={industriesText}
          onChange={(e) => setIndustriesText(e.target.value)}
          placeholder="restaurantes, salud, solar"
        />
      </label>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">price setup USD</span>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={form.priceSetupUsd}
            onChange={(e) => setForm({ ...form, priceSetupUsd: parseFloat(e.target.value) || 0 })}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">price MRR USD</span>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={form.priceMrrUsd}
            onChange={(e) => setForm({ ...form, priceMrrUsd: parseFloat(e.target.value) || 0 })}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">
            costs setup USD {form.providers.length > 0 && '(suma providers)'}
          </span>
          <Input
            type="number"
            step="0.01"
            min="0"
            disabled={form.providers.length > 0}
            value={form.providers.length > 0 ? totalCostsSetup : form.costsSetupUsd}
            onChange={(e) => setForm({ ...form, costsSetupUsd: parseFloat(e.target.value) || 0 })}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs font-medium uppercase text-muted-foreground">
            costs MRR USD {form.providers.length > 0 && '(suma providers)'}
          </span>
          <Input
            type="number"
            step="0.01"
            min="0"
            disabled={form.providers.length > 0}
            value={form.providers.length > 0 ? totalCostsMrr : form.costsMrrUsd}
            onChange={(e) => setForm({ ...form, costsMrrUsd: parseFloat(e.target.value) || 0 })}
          />
        </label>
      </div>

      {/* ─── Metering / Usage ─── */}
      <div className="rounded border bg-muted/20 p-3">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Metering · usage cap incluido en MRR + overage
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <label className="space-y-1">
            <span className="text-xs font-medium uppercase text-muted-foreground">unidad</span>
            <select
              className="block w-full rounded border bg-background px-2 py-2 text-sm"
              value={form.unitLabel}
              onChange={(e) => setForm({ ...form, unitLabel: e.target.value as UnitLabel })}
            >
              <option value="voice_minute">voice_minute</option>
              <option value="conversation">conversation</option>
              <option value="report">report</option>
              <option value="contact">contact</option>
              <option value="none">none (sin medición)</option>
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium uppercase text-muted-foreground">incluido/MRR</span>
            <Input
              type="number"
              min="0"
              value={form.includedPerMrr}
              onChange={(e) => setForm({ ...form, includedPerMrr: parseInt(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium uppercase text-muted-foreground">tokens/uso</span>
            <Input
              type="number"
              min="0"
              value={form.tokensPerUse}
              onChange={(e) => setForm({ ...form, tokensPerUse: parseInt(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium uppercase text-muted-foreground">
              overage USD
            </span>
            <Input
              type="number"
              step="0.0001"
              min="0"
              value={form.overagePriceUsd}
              onChange={(e) => setForm({ ...form, overagePriceUsd: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium uppercase text-muted-foreground">
              costo overage
            </span>
            <Input
              type="number"
              step="0.0001"
              min="0"
              value={form.overageCostUsd}
              onChange={(e) => setForm({ ...form, overageCostUsd: parseFloat(e.target.value) || 0 })}
            />
          </label>
        </div>
        {form.includedPerMrr > 0 && form.tokensPerUse > 0 && (
          <div className="mt-2 text-xs text-muted-foreground">
            Est. tokens/mes incluidos: {(form.includedPerMrr * form.tokensPerUse).toLocaleString()}
            {form.overagePriceUsd > 0 && form.overageCostUsd > 0 && (
              <>
                {' · '}margen overage:{' '}
                {(((form.overagePriceUsd - form.overageCostUsd) / form.overagePriceUsd) * 100).toFixed(0)}%
              </>
            )}
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium uppercase text-muted-foreground">
            providers per cost
          </span>
          <button
            type="button"
            onClick={addProvider}
            className="text-xs text-primary hover:underline"
          >
            + provider
          </button>
        </div>
        <div className="space-y-2">
          {form.providers.map((p, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 rounded border bg-background p-2">
              <Input
                className="col-span-4"
                placeholder="provider · vapi, anthropic, infra"
                value={p.provider}
                onChange={(e) => updateProvider(i, { provider: e.target.value })}
              />
              <Input
                className="col-span-3"
                type="number"
                step="0.01"
                placeholder="cost setup"
                value={p.costSetup}
                onChange={(e) => updateProvider(i, { costSetup: parseFloat(e.target.value) || 0 })}
              />
              <Input
                className="col-span-3"
                type="number"
                step="0.01"
                placeholder="cost MRR"
                value={p.costMrr}
                onChange={(e) => updateProvider(i, { costMrr: parseFloat(e.target.value) || 0 })}
              />
              <button
                type="button"
                onClick={() => removeProvider(i)}
                className="col-span-2 text-xs text-muted-foreground hover:text-red-600"
              >
                eliminar
              </button>
            </div>
          ))}
          {form.providers.length === 0 && (
            <p className="text-xs text-muted-foreground">
              sin breakdown · escribe costs setup/MRR directos arriba
            </p>
          )}
        </div>
      </div>

      {/* Required Resources */}
      <div className="space-y-2 rounded-lg border bg-card p-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase text-muted-foreground">
            recursos requeridos · qué necesita el cliente para provisionar este agente
          </span>
          <button
            type="button"
            onClick={() =>
              setForm({
                ...form,
                requiredResources: [
                  ...form.requiredResources,
                  { resourceId: '', name: '', type: 'LLM', mandatory: true },
                ],
              })
            }
            className="text-xs text-primary hover:underline"
          >
            + recurso
          </button>
        </div>
        <div className="space-y-2">
          {form.requiredResources.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 rounded border bg-background p-2">
              <Input
                className="col-span-3"
                placeholder="id (openai_api)"
                value={r.resourceId}
                onChange={(e) =>
                  setForm({
                    ...form,
                    requiredResources: form.requiredResources.map((x, idx) =>
                      idx === i ? { ...x, resourceId: e.target.value } : x,
                    ),
                  })
                }
              />
              <Input
                className="col-span-3"
                placeholder="nombre (OpenAI API)"
                value={r.name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    requiredResources: form.requiredResources.map((x, idx) =>
                      idx === i ? { ...x, name: e.target.value } : x,
                    ),
                  })
                }
              />
              <select
                className="col-span-2 rounded border bg-background px-2 text-sm"
                value={r.type}
                onChange={(e) =>
                  setForm({
                    ...form,
                    requiredResources: form.requiredResources.map((x, idx) =>
                      idx === i ? { ...x, type: e.target.value } : x,
                    ),
                  })
                }
              >
                <option value="LLM">LLM</option>
                <option value="TTS">TTS</option>
                <option value="TELEPHONY">TELEPHONY</option>
                <option value="CHANNEL">CHANNEL</option>
                <option value="PAYMENT">PAYMENT</option>
                <option value="DATA_SOURCE">DATA_SOURCE</option>
                <option value="CRM">CRM</option>
              </select>
              <Input
                className="col-span-2"
                placeholder="setup URL"
                value={r.setupUrl ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    requiredResources: form.requiredResources.map((x, idx) =>
                      idx === i ? { ...x, setupUrl: e.target.value || null } : x,
                    ),
                  })
                }
              />
              <label className="col-span-1 flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={r.mandatory}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      requiredResources: form.requiredResources.map((x, idx) =>
                        idx === i ? { ...x, mandatory: e.target.checked } : x,
                      ),
                    })
                  }
                />
                req
              </label>
              <button
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    requiredResources: form.requiredResources.filter((_, idx) => idx !== i),
                  })
                }
                className="col-span-1 text-xs text-muted-foreground hover:text-red-600"
              >
                ×
              </button>
            </div>
          ))}
          {form.requiredResources.length === 0 && (
            <p className="text-xs text-muted-foreground">
              sin recursos · agrega para que SetupWizard sepa qué pedirle al cliente
            </p>
          )}
        </div>
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-medium uppercase text-muted-foreground">notes</span>
        <textarea
          className="w-full rounded border bg-background px-3 py-2 text-sm"
          rows={2}
          value={form.notes ?? ''}
          onChange={(e) => setForm({ ...form, notes: e.target.value || null })}
        />
      </label>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" type="button" onClick={onCancel}>
          cancelar
        </Button>
        <Button size="sm" type="submit" disabled={submitting}>
          {submitting ? 'guardando…' : editingId ? 'guardar cambios' : 'crear agente'}
        </Button>
      </div>
    </form>
  );
}
